import { openDB, type IDBPDatabase } from 'idb';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ShoppingListDto } from '../api/shoppingTypes.js';

export type SyncStatus = 'offline' | 'syncing' | 'synced';

export type PendingMutation = (ToggleMutation | AddMutation) & { id?: number };

export type MutationExecutor = (m: PendingMutation) => Promise<void>;

export const OFFLINE_DB_NAME = 'chefcook:offline:v1';

let dbPromise: Promise<IDBPDatabase> | null = null;

export function getOfflineDb(): Promise<IDBPDatabase> {
  if (dbPromise === null) {
    dbPromise = openDB(OFFLINE_DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('lists')) {
          db.createObjectStore('lists', { keyPath: 'list_id' });
        }
        if (!db.objectStoreNames.contains('mutations')) {
          db.createObjectStore('mutations', {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
      },
    });
  }
  return dbPromise;
}

export async function mirrorList(dto: ShoppingListDto): Promise<void> {
  try {
    const db = await getOfflineDb();
    await db.put('lists', { list_id: dto.list_id, dto });
  } catch {
    // ignore - storage unavailable
  }
}

export async function loadMirror(
  listId: number,
): Promise<ShoppingListDto | null> {
  try {
    const db = await getOfflineDb();
    const row = (await db.get('lists', listId)) as
      | { list_id: number; dto: ShoppingListDto }
      | undefined;
    return row ? row.dto : null;
  } catch {
    return null;
  }
}

export async function enqueueMutation(m: Omit<PendingMutation, 'id'>): Promise<void> {
  try {
    const db = await getOfflineDb();
    await db.add('mutations', m);
  } catch {
    // ignore
  }
}

export async function readPendingMutations(): Promise<
  Array<PendingMutation & { id: number }>
> {
  try {
    const db = await getOfflineDb();
    return (await db.getAll('mutations')) as Array<
      PendingMutation & { id: number }
    >;
  } catch {
    return [];
  }
}

export async function deleteMutation(id: number): Promise<void> {
  try {
    const db = await getOfflineDb();
    await db.delete('mutations', id);
  } catch {
    // ignore
  }
}

export interface UseOfflineSyncResult {
  status: SyncStatus;
  pending: number;
  enqueue: (m: ToggleMutation | AddMutation) => Promise<void>;
  flush: () => Promise<void>;
  isOnline: boolean;
}

export interface ToggleMutation {
  kind: 'toggle';
  item_id: number;
  is_checked: boolean;
}

export interface AddMutation {
  kind: 'add';
  payload: {
    list_id: number;
    name: string;
    amount: number;
    unit: string;
  };
}

export function useOfflineSync(
  executor: MutationExecutor,
): UseOfflineSyncResult {
  const [online, setOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const [pending, setPending] = useState<number>(0);
  const [status, setStatus] = useState<SyncStatus>(
    typeof navigator === 'undefined' ? 'synced' : navigator.onLine ? 'synced' : 'offline',
  );
  const executorRef = useRef<MutationExecutor>(executor);
  executorRef.current = executor;

  const refreshPending = useCallback(async (): Promise<void> => {
    const all = await readPendingMutations();
    setPending(all.length);
  }, []);

  const flush = useCallback(async (): Promise<void> => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    setStatus('syncing');
    try {
      const all = await readPendingMutations();
      for (const m of all) {
        try {
          await executorRef.current(m);
          await deleteMutation(m.id);
        } catch {
          break;
        }
      }
      await refreshPending();
    } finally {
      setStatus(
        typeof navigator !== 'undefined' && !navigator.onLine
          ? 'offline'
          : 'synced',
      );
    }
  }, [refreshPending]);

  useEffect(() => {
    function handleOnline(): void {
      setOnline(true);
      void flush();
    }
    function handleOffline(): void {
      setOnline(false);
      setStatus('offline');
    }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    void refreshPending();
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      void flush();
    }
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [flush, refreshPending]);

  const enqueue = useCallback(
    async (m: Omit<PendingMutation, 'id'>): Promise<void> => {
      await enqueueMutation(m);
      await refreshPending();
      setStatus('offline');
    },
    [refreshPending],
  );

  return { status, pending, enqueue, flush, isOnline: online };
}
